import React, { useEffect, useRef, useState } from "react";
import { RecIcon } from "../materials/MaterialRecordParts";
import { ProjectOfflineBadge } from "./ProjectOfflineBadge";
import { isProjectArchived } from "../../helpers/projectVisibility";
import "../materials/materialRecord.css";
import "./projects.css";

export function projectClientName(project) {
  return project?.clients?.client_name || project?.client_name || "";
}

function formatTargetDate(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ""));
  if (!match) return "";
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[Number(match[2]) - 1]} ${Number(match[3])}, ${match[1]}`;
}

export function formatProjectStatus(status) {
  if (!status) return "";
  return String(status)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * Project SELECTOR card. It identifies the project (name, client, status,
 * favorite, offline state) and opens it. No costs: those live in the project.
 * Favorites and All Projects use this same card.
 */
export function ProjectCard({
  project,
  onSelect,
  onArchive,
  onRestore,
  favorite = false,
  favoriteState = "",
  onToggleFavorite,
  onRetryFavorite,
  taskStats,
  valueLabel = "",
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const archived = isProjectArchived(project);
  const client = projectClientName(project);
  const name = project.project_name || "Unnamed Project";
  const status = archived ? "Archived" : formatProjectStatus(project.status) || "Active";
  const attention = favorite && favoriteState === "attention";

  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (event) => {
      if (event.type === "keydown") {
        if (event.key === "Escape") setMenuOpen(false);
        return;
      }
      if (!menuRef.current?.contains(event.target)) setMenuOpen(false);
    };
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", close);
    };
  }, [menuOpen]);

  return (
    <div
      className={`pj-card${favorite ? " is-favorite" : ""}${archived ? " is-archived" : ""}`}
      data-testid="project-card"
    >
      <div className="pj-card__row">
        <button
          type="button"
          className="pj-card__main"
          onClick={() => onSelect(project.project_id)}
          aria-label={`Open project ${name}`}
        >
          <span className="pj-card__tile" aria-hidden="true">
            <RecIcon name="building" />
          </span>
          <span className="pj-card__text">
            <span className="pj-card__name">{name}</span>
            {client ? <span className="pj-card__client">{client}</span> : null}
            {project.site_address ? (
              <span className="pj-card__addr">{project.site_address}</span>
            ) : null}
            <span className="pj-card__meta">
              <span className={`pj-status${archived ? " pj-status--archived" : ""}`}>{status}</span>
              {favorite && !attention ? (
                <ProjectOfflineBadge state={favoriteState} />
              ) : null}
            </span>
            {/* Desktop-only (hidden <1024px): real task counts, zero chips stay visible. */}
            <span className={`pj-card__progress${taskStats ? "" : " pj-card__progress--na"}`} data-testid="project-card-progress">
              {taskStats ? (
                <>
                  <span className="pj-card__bar" aria-hidden="true">
                    <span
                      style={{
                        width: `${taskStats.total ? Math.round((taskStats.completed / taskStats.total) * 100) : 0}%`,
                      }}
                    />
                  </span>
                  <span className="pj-card__pct">
                    {taskStats.total ? `${Math.round((taskStats.completed / taskStats.total) * 100)}%` : "—"}
                  </span>
                  <span className="pj-card__tasks">
                    {taskStats.completed} / {taskStats.total} tasks
                  </span>
                </>
              ) : (
                <span className="pj-card__tasks">Task progress unavailable</span>
              )}
            </span>
            {taskStats ? (
              <span className="pj-card__chips">
                {/* Phones hide zero-count chips (compact scan); desktop keeps all three. */}
                <span className={`pj-tchip pj-tchip--red${taskStats.overdue ? "" : " is-zero"}`}>{taskStats.overdue} overdue</span>
                <span className={`pj-tchip pj-tchip--gold${taskStats.today ? "" : " is-zero"}`}>{taskStats.today} today</span>
                <span className={`pj-tchip pj-tchip--blue${taskStats.upcoming ? "" : " is-zero"}`}>{taskStats.upcoming} upcoming</span>
                {formatTargetDate(project.estimated_end_date) ? (
                  <span className="pj-card__target">Target {formatTargetDate(project.estimated_end_date)}</span>
                ) : null}
              </span>
            ) : null}
          </span>
        </button>
        <RecIcon name="chevron" className="pj-card__chevron" />

        <div className="pj-card__actions">
          <button
            type="button"
            className={`pj-iconbtn pj-star${favorite ? " is-on" : ""}`}
            data-testid="project-favorite-toggle"
            aria-pressed={favorite}
            aria-label={favorite ? `Remove favorite ${name}` : `Favorite ${name}`}
            onClick={(event) => {
              event.stopPropagation();
              onToggleFavorite?.(project.project_id);
            }}
          >
            <RecIcon name="star" />
          </button>
          <div className="pj-menuwrap" ref={menuRef}>
            <button
              type="button"
              className="pj-iconbtn"
              data-testid="project-card-menu"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-label={`More actions for ${name}`}
              onClick={(event) => {
                event.stopPropagation();
                setMenuOpen((open) => !open);
              }}
            >
              <RecIcon name="more" className="pj-more-icon" />
            </button>
            {menuOpen ? (
              <div className="mat-menu pj-menu" role="menu">
                {archived ? (
                  <button
                    type="button"
                    role="menuitem"
                    className="mat-menu__item project-hub-card__restore-btn"
                    onClick={() => {
                      setMenuOpen(false);
                      onRestore(project.project_id);
                    }}
                  >
                    <span>Restore project</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    role="menuitem"
                    className="mat-menu__item pj-menu-danger project-hub-card__archive-btn"
                    onClick={() => {
                      setMenuOpen(false);
                      onArchive(project.project_id);
                    }}
                  >
                    <span>Archive project</span>
                  </button>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="pj-card__foot">
        <span>
          <em>Target</em>
          <b>{formatTargetDate(project.estimated_end_date) || "Not set"}</b>
        </span>
        <span>
          <em>Value</em>
          <b>{valueLabel || "—"}</b>
        </span>
        <span>
          <em>Team</em>
          <b>
            {taskStats
              ? taskStats.assigneeCount
                ? `${taskStats.assigneeCount} assigned`
                : "Unassigned"
              : "—"}
          </b>
        </span>
      </div>

      {attention ? (
        <ProjectOfflineBadge
          state={favoriteState}
          block
          onRetry={() => onRetryFavorite?.(project.project_id)}
        />
      ) : null}
    </div>
  );
}
